// discount-allocation.ts — INTERNAL split of the general discount, pro rata of price (book 4.10 §5).
//
// The discount AMOUNT is the result of a global calculation (see `target-discount.ts`). This split does not
// recompute it — it SPREADS that settled amount line by line, for three internal uses: per-line margin,
// the future BCF, and the displayed effective discount. Its rounding NEVER affects the total.
//
// PRO RATA OF PRICE: each line concedes R × (its total / total of the included lines), a UNIFORM rate
// across the base whatever the line's margin. Simple to explain to the client and it hides no
// cross-subsidy, but it has an accepted cost: a low-margin line can drop below its purchase cost, hence
// the guard below, which NAMES such lines instead of diluting them.
//
// The module does not know what a print line, a service or an incidental cost is: the caller passes the
// already-filtered base entries. `compute()` owns the definition of the base, in one place.

import { round2 } from "../core/core";

/** An entry in the general discount base — print line, linked line, service… */
export interface AllocationEntry {
  /** Displayable marker (line number, service code) — used to NAME the entry in the summary. */
  key: string;
  /** Readable label, reused as is in the submission summary. */
  label?: string;
  /** Total excl. VAT BEFORE the general discount. */
  total: number;
  /** TOTAL minimum unit price floor of the entry. Absent or 0 = nothing protects it (services). */
  floorTotal?: number;
  /** Total purchase cost. Absent = PA unknown → no margin guard possible on this entry. */
  pa?: number;
}

/** An entry whose discounted total fell BELOW its purchase cost. */
export interface SubPaEntry {
  key: string;
  label?: string;
  /** Total after discount. */
  totalDiscount: number;
  /** Purchase cost. */
  pa: number;
  /** What the line loses: PA − discounted total, always > 0. */
  loss: number;
}

/** Result of the discount allocation. */
export interface AllocationResult {
  /** Concession of each entry, IN INPUT ORDER (no keys, so no collision possible). */
  concessions: number[];
  /** Sum actually spread — equal to the requested amount unless every floor is saturated. */
  allocated: number;
  /** What could not be spread (all floors reached). Zero in normal use. */
  remainder: number;
  /** Number of passes consumed (1 without a blocking floor, up to 5). */
  passes: number;
  /** Entries that fell below their PA — NAMED, with their loss. Not blocking on its own. */
  belowPa: SubPaEntry[];
}

/** Five passes maximum, then the residue goes to the largest entry. */
const MAX_PASSES = 5;

/**
 * Spreads `amount` over `entries` pro rata of their total.
 *
 * FLOOR PROTECTION (minima box unticked): an entry never drops below its minimum unit price floor. What
 * could not be allocated to it is REDISTRIBUTED on the next pass, pro rata of price, over the entries
 * still free — otherwise the discount would not come out exact and the total would drift. Five passes at
 * most; the surviving cent residue goes to the included entry with the largest total.
 *
 * Minima box TICKED: `includeMinima` neutralises all floors, a single pass is enough.
 * @param entries The entries of the general discount base.
 * @param amount The settled discount amount to spread.
 * @param includeMinima Whether the minima box is ticked (floors neutralised).
 * @returns The concession of each entry, the allocated sum, the remainder and the entries below their PA.
 */
export function allocateDiscount(
  entries: readonly AllocationEntry[],
  amount: number,
  includeMinima = false,
): AllocationResult {
  const n = entries.length;
  const concessions = new Array<number>(n).fill(0);
  const target = round2(Math.max(0, amount));
  if (target <= 0 || n === 0) return { concessions, allocated: 0, remainder: round2(target), passes: 0, belowPa: [] };

  // Capacity of each entry: what it can concede before hitting its floor.
  const capacity = entries.map((e) => {
    const total = Math.max(0, e.total);
    if (includeMinima) return total; // nothing protects any more: the line can go down to zero
    const floor = Math.min(Math.max(0, e.floorTotal ?? 0), total);
    return round2(total - floor);
  });

  let left = target;
  let passes = 0;
  while (left > 0.005 && passes < MAX_PASSES) {
    passes++;
    // Base of THIS pass: the entries that still have capacity.
    const free: number[] = [];
    let base = 0;
    for (let i = 0; i < n; i++) {
      if (capacity[i]! - concessions[i]! > 0.005 && entries[i]!.total > 0) { free.push(i); base += entries[i]!.total; }
    }
    if (!free.length || base <= 0) break;

    const toAllocate = left;
    for (const i of free) {
      const part = round2(toAllocate * (entries[i]!.total / base));
      const available = round2(capacity[i]! - concessions[i]!);
      const given = Math.min(part, available);
      if (given <= 0) continue;
      concessions[i] = round2(concessions[i]! + given);
      left = round2(left - given);
      if (left <= 0) break;
    }
    if (left <= 0.005) break;
  }

  // Cent RESIDUE: it goes to the included entry with the largest total.
  //
  // ONLY THE RESIDUE. Absorbing without a cap would BREACH a floor: on a base saturated at €200 of
  // capacity, a €500 amount would force €300 onto one line, far below its minimum unit price. That is
  // not a rounding, it is a STRUCTURAL shortfall, and it must show up in `remainder`. The absorption cap
  // is worth what a rounding can leave: one cent per entry and per pass.
  const residueCap = Math.max(0.05, round2(n * MAX_PASSES * 0.01));
  if (left > 0.005 && left <= residueCap) {
    let largest = -1;
    for (let i = 0; i < n; i++) if (largest < 0 || entries[i]!.total > entries[largest]!.total) largest = i;
    if (largest >= 0 && entries[largest]!.total > 0) {
      concessions[largest] = round2(concessions[largest]! + left);
      left = 0;
    }
  }

  const allocated = round2(concessions.reduce((s, c) => s + c, 0));

  // NEGATIVE MARGIN GUARD — a direct consequence of the price pro rata, accepted but never silent.
  const belowPa: SubPaEntry[] = [];
  for (let i = 0; i < n; i++) {
    const e = entries[i]!;
    if (e.pa == null) continue; // PA unknown: nothing can be asserted, so nothing is claimed
    const totalDiscount = round2(e.total - concessions[i]!);
    if (totalDiscount < round2(e.pa)) {
      belowPa.push({
        key: e.key,
        ...(e.label != null ? { label: e.label } : {}),
        totalDiscount,
        pa: round2(e.pa),
        loss: round2(e.pa - totalDiscount),
      });
    }
  }

  return { concessions, allocated, remainder: round2(Math.max(0, left)), passes, belowPa };
}

/**
 * Effective discount rate of an entry: its concession over its total.
 * @param total The entry total.
 * @param concession The discount conceded by the entry.
 * @returns The effective rate (0 when the total is not positive).
 */
export function effectiveRate(total: number, concession: number): number {
  return total > 0 ? concession / total : 0;
}
